import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { useI18n } from '../i18n/index.jsx';

// Google address autocomplete (Places API (New), PlaceAutocompleteElement).
// Picking a suggestion calls onPlace({ addressLine, city, region,
// postalCode, country }) so the form fills every address field; the plain
// fields stay editable. Shown only when GOOGLE_MAPS_API_KEY is configured;
// onState('loading' | 'ready' | 'off') lets the form decide whether to
// show the plain fields straight away.

let configPromise = null;
let mapsPromise = null;
function loadMaps(key, lang) {
  mapsPromise ||= new Promise((resolve, reject) => {
    if (window.google?.maps?.importLibrary) return resolve();
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&loading=async&v=weekly&language=${lang}&callback=__pmeMapsReady`;
    s.async = true;
    window.__pmeMapsReady = () => resolve();
    s.onerror = () => { mapsPromise = null; reject(new Error('maps_load_failed')); };
    document.head.appendChild(s);
  });
  return mapsPromise;
}

function parse(components) {
  const get = (type, short) => {
    const c = components.find((x) => x.types.includes(type));
    return c ? (short ? c.shortText : c.longText) : '';
  };
  return {
    addressLine: [get('street_number'), get('route')].filter(Boolean).join(' ') || get('premise'),
    city: get('locality') || get('postal_town') || get('sublocality') || get('administrative_area_level_2'),
    region: get('administrative_area_level_1', true),
    postalCode: get('postal_code'),
    country: get('country', true),
  };
}

export default function AddressAutocomplete({ onPlace, onState, disabled, children }) {
  const { t, lang } = useI18n();
  const box = useRef(null);
  const handler = useRef(onPlace);
  handler.current = onPlace;
  const stateRef = useRef(onState);
  stateRef.current = onState;
  const [state, setStateRaw] = useState('loading');
  const setState = (s) => { setStateRaw(s); stateRef.current?.(s); };

  useEffect(() => {
    let alive = true;
    configPromise ||= api('/auth/config').catch(() => ({}));
    configPromise.then(async (cfg) => {
      if (!cfg.googleMapsApiKey) { if (alive) setState('off'); return; }
      await loadMaps(cfg.googleMapsApiKey, lang === 'en' ? 'en' : 'fr');
      const { PlaceAutocompleteElement } = await window.google.maps.importLibrary('places');
      if (!alive || !box.current) return;
      const el = new PlaceAutocompleteElement();
      el.setAttribute('placeholder', t('company.addressSearchPlaceholder'));
      const pick = async (place) => {
        await place.fetchFields({ fields: ['addressComponents'] });
        handler.current(parse(place.addressComponents || []));
      };
      // Current event, and the older beta one, in case Google serves it.
      el.addEventListener('gmp-select', ({ placePrediction }) => { if (placePrediction) pick(placePrediction.toPlace()).catch(() => {}); });
      el.addEventListener('gmp-placeselect', ({ place }) => { if (place) pick(place).catch(() => {}); });
      box.current.replaceChildren(el);
      setState('ready');
    }).catch(() => alive && setState('off'));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, t]);

  if (state === 'off') return null;
  return (
    <div className="field">
      <label>{t('company.addressSearch')}</label>
      <div ref={box} className={`places-box${disabled ? ' is-disabled' : ''}`} />
      <p className="field-hint">{t('company.addressSearchHint')} {children}</p>
    </div>
  );
}
